/**
 * The Arche Store's app categories, for the client: the category picker in the
 * Arche Builder and Fias Forge, and the store's category pages.
 *
 * A copy of `APP_CATEGORIES` in `@fias/db-types` (the client does not depend on
 * that package). The server is the authority — it refuses an unknown category —
 * so drift would never be a hole, but it would be a lie: a picker offering a
 * category the publish refuses, or hiding one it accepts. Pinned by
 * `tests/architecture/app-categories-client-parity.test.ts`. Ids are permanent.
 */

export interface AppCategoryOption {
  id: string;
  label: string;
}

export const APP_CATEGORY_OPTIONS: readonly AppCategoryOption[] = [
  { id: 'ai-assistants', label: 'AI Assistants' },
  { id: 'productivity', label: 'Productivity' },
  { id: 'business', label: 'Business' },
  { id: 'finance', label: 'Finance' },
  { id: 'communication', label: 'Communication' },
  { id: 'social', label: 'Social' },
  { id: 'graphics-design', label: 'Graphics & Design' },
  { id: 'photo-video', label: 'Photo & Video' },
  { id: 'music-audio', label: 'Music & Audio' },
  { id: 'education', label: 'Education' },
  { id: 'developer-tools', label: 'Developer Tools' },
  { id: 'utilities', label: 'Utilities' },
  { id: 'entertainment', label: 'Entertainment' },
  { id: 'lifestyle', label: 'Lifestyle' },
  { id: 'health-fitness', label: 'Health & Fitness' },
  { id: 'travel', label: 'Travel' },
  { id: 'food-drink', label: 'Food & Drink' },
  { id: 'shopping', label: 'Shopping' },
  { id: 'news', label: 'News' },
  { id: 'reference', label: 'Reference' },
  { id: 'sports', label: 'Sports' },
];

/** An app names at most this many categories; the first is its primary one. */
export const APP_MAX_CATEGORIES = 2;

const LABEL_BY_ID = new Map(APP_CATEGORY_OPTIONS.map((c) => [c.id, c.label]));

/** The label for a category id, or the id itself for one this copy doesn't know. */
export function appCategoryLabel(id: string): string {
  return LABEL_BY_ID.get(id) ?? id;
}

/**
 * The selection after toggling one category: on goes to the end (so the first
 * one picked stays primary), off removes it. A toggle that would pass the
 * limit is ignored rather than dropping another.
 */
export function toggleAppCategory(selected: readonly string[], id: string): string[] {
  if (selected.includes(id)) return selected.filter((c) => c !== id);
  if (selected.length >= APP_MAX_CATEGORIES) return [...selected];
  return [...selected, id];
}
